// The "more" (⋮) menu shared by the composer, the interactive VoicePills, and hands-free.
// Collapsed it shows compact rows: Captions (toggle), Hands-free (action, when offered), Speaker and
// Mic. Speaker/Mic EXPAND on click to reveal their device list (image-1 picker); CC and Hands-free
// are simple rows. Output-device switching is Chromium-desktop only — elsewhere the speaker section
// shows a short note (the OS controls routing).
import { useEffect, useRef, useState } from 'react';
import { MoreIcon, CheckIcon, CaptionIcon, MicIcon, SpeakerIcon, HandsFreeIcon, ChevronDownIcon } from '@/components/chat/ChatIcons';
import type { AudioDevice } from '@/hooks/useAudioDevices';

export interface AudioMoreMenuProps {
  captionsOn: boolean;
  onToggleCaptions: () => void;
  inputs: AudioDevice[];
  outputs: AudioDevice[];
  outputSupported: boolean;
  /** Selected device ids ('' = system default). */
  inputId: string;
  outputId: string;
  onSelectInput: (id: string) => void;
  onSelectOutput: (id: string) => void;
  /** Called when the menu opens — refresh the device list / labels. */
  onOpen?: () => void;
  /** Where the popover aligns relative to the trigger. */
  align?: 'left' | 'right';
  /** When provided, a "Hands-free" row enters hands-free mode (composer, mobile only). */
  onEnterHandsFree?: () => void;
  /** Override the trigger button's size/surface (e.g. to match a tall End button in hands-free). */
  triggerClassName?: string;
}

const DEFAULT_DEVICE: AudioDevice = { deviceId: '', label: 'System default' };

const deviceLabel = (list: AudioDevice[], id: string): string =>
  id ? (list.find((d) => d.deviceId === id)?.label || 'Selected device') : 'System default';

const DeviceRow = ({ device, selected, onClick }: { device: AudioDevice; selected: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between gap-2 rounded-lg pl-9 pr-3 py-1.5 text-left text-sm transition ${
      selected ? 'text-ui-text font-medium' : 'text-ui-text2 hover:bg-ui-hover'
    }`}
  >
    <span className="truncate">{device.label}</span>
    {selected && <CheckIcon className="h-4 w-4 flex-shrink-0 text-brand-bright" />}
  </button>
);

export const AudioMoreMenu = ({
  captionsOn,
  onToggleCaptions,
  inputs,
  outputs,
  outputSupported,
  inputId,
  outputId,
  onSelectInput,
  onSelectOutput,
  onOpen,
  align = 'right',
  onEnterHandsFree,
  triggerClassName,
}: AudioMoreMenuProps) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<'mic' | 'speaker' | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      setExpanded(null);
      return;
    }
    onOpen?.();
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const rowBtn = 'w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-ui-text hover:bg-ui-hover transition';

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        title="More"
        aria-label="More options"
        className={`rounded-full flex items-center justify-center transition ${triggerClassName || 'w-9 h-9'} ${
          open ? 'bg-ui-hover text-ui-text' : 'text-ui-text2 hover:text-ui-text'
        }`}
      >
        <MoreIcon className="h-5 w-5" />
      </button>

      {open && (
        <div
          className={`absolute bottom-full mb-2 z-50 w-64 max-h-[60vh] overflow-y-auto no-scrollbar rounded-2xl border border-ui-border bg-ui-card shadow-xl p-1.5 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {/* Captions — simple toggle */}
          <button onClick={onToggleCaptions} className={rowBtn}>
            <span className="flex items-center gap-2"><CaptionIcon className="h-4 w-4" /> Captions</span>
            <span className={`text-xs font-medium ${captionsOn ? 'text-brand-bright' : 'text-gray-500'}`}>{captionsOn ? 'On' : 'Off'}</span>
          </button>

          {/* Hands-free — simple action (composer only) */}
          {onEnterHandsFree && (
            <button onClick={() => { setOpen(false); onEnterHandsFree(); }} className={rowBtn}>
              <span className="flex items-center gap-2"><HandsFreeIcon className="h-4 w-4" /> Hands-free</span>
            </button>
          )}

          {/* Speaker — expand to pick the output device */}
          <button onClick={() => setExpanded((e) => (e === 'speaker' ? null : 'speaker'))} className={rowBtn}>
            <span className="flex items-center gap-2 min-w-0"><SpeakerIcon className="h-4 w-4 flex-shrink-0" /> Speaker</span>
            <span className="flex items-center gap-1 min-w-0 text-xs text-gray-400">
              <span className="truncate max-w-[90px] text-ui-text2">{outputSupported ? deviceLabel(outputs, outputId) : 'System'}</span>
              <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${expanded === 'speaker' ? 'rotate-180' : ''}`} />
            </span>
          </button>
          {expanded === 'speaker' && (
            outputSupported ? (
              [DEFAULT_DEVICE, ...outputs].map((d) => (
                <DeviceRow key={`out-${d.deviceId}`} device={d} selected={outputId === d.deviceId}
                  onClick={() => { onSelectOutput(d.deviceId); setExpanded(null); }} />
              ))
            ) : (
              <p className="pl-9 pr-3 py-1.5 text-xs text-ui-text3">Your device controls audio output.</p>
            )
          )}

          {/* Microphone — expand to pick the input device */}
          <button onClick={() => setExpanded((e) => (e === 'mic' ? null : 'mic'))} className={rowBtn}>
            <span className="flex items-center gap-2 min-w-0"><MicIcon className="h-4 w-4 flex-shrink-0" /> Mic</span>
            <span className="flex items-center gap-1 min-w-0 text-xs text-gray-400">
              <span className="truncate max-w-[90px] text-ui-text2">{deviceLabel(inputs, inputId)}</span>
              <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${expanded === 'mic' ? 'rotate-180' : ''}`} />
            </span>
          </button>
          {expanded === 'mic' && (
            [DEFAULT_DEVICE, ...inputs].map((d) => (
              <DeviceRow key={`in-${d.deviceId}`} device={d} selected={inputId === d.deviceId}
                onClick={() => { onSelectInput(d.deviceId); setExpanded(null); }} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AudioMoreMenu;
